import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

import {
  ContentTagOption,
  House,
  ProjectBudgetType,
} from "../../store/apis/commercialProposalApi";
// El color vive en el tablero, que es donde se definio: importarlo evita dos listas de
// hex que se desincronizan.
import { HOUSE_COLOR } from "./ProposalBoard";

/** Un escenario en el formulario. Los importes son texto: el usuario escribe. */
export interface ScenarioDraft {
  label: string;
  amount: string;
  isReference: boolean;
}

export interface ProjectDraft {
  lineName: string;
  house: House | "";
  budgetType: ProjectBudgetType;
  budget: string;
  contentTags: string[];
  scenarios: ScenarioDraft[];
}

export const MAX_SCENARIOS = 3;

export const HOUSES: House[] = [
  "HOUSE_OF_DIGITAL",
  "HOUSE_OF_AI",
  "HOUSE_OF_MEDIA",
  "HOUSE_OF_STORIES",
];

export const HOUSE_LABEL: Record<string, string> = {
  HOUSE_OF_DIGITAL: "House of Digital",
  HOUSE_OF_AI: "House of AI",
  HOUSE_OF_MEDIA: "House of Media",
  HOUSE_OF_STORIES: "House of Stories",
};

export const emptyProject = (): ProjectDraft => ({
  lineName: "",
  house: "",
  budgetType: "DEFINIDO",
  budget: "",
  contentTags: [],
  scenarios: [],
});

const toNumber = (value: string): number => {
  // Se acepta la coma decimal, que es lo que escribe cualquiera en español.
  const normalized = value.replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * El importe que cuenta para el total de la propuesta.
 *
 * Misma regla que el backend: en `definido` y `estimado` es el presupuesto, en
 * `escenarios` el del escenario de referencia. Está duplicada a propósito para poder
 * pintar el total mientras se escribe, pero el que se guarda lo calcula el servidor: si
 * las dos cuentas discrepan, manda la suya.
 */
export const effectiveAmount = (project: ProjectDraft): number => {
  if (project.budgetType === "ESCENARIOS") {
    const reference = project.scenarios.find(
      (scenario) => scenario.isReference
    );
    return reference ? toNumber(reference.amount) : 0;
  }
  return toNumber(project.budget);
};

/**
 * Los tags que se pueden elegir en una línea: los activos de su House en House Group, o
 * los del catálogo de BeApolo. Sin House elegida todavía, ninguno.
 */
export const tagsForLine = (
  tags: ContentTagOption[],
  showHouses: boolean,
  house: House | ""
): ContentTagOption[] =>
  tags.filter(
    (tag) =>
      tag.active &&
      (showHouses
        ? tag.businessUnit === "HOUSE_GROUP" && tag.house === house
        : tag.businessUnit === "BEAPOLO")
  );

/**
 * Quita de una línea los tags que ya no le corresponden: los de otra House al cambiarla,
 * o los retirados que trae una versión anterior. El backend los rechazaría.
 */
export const keepLineTags = (
  contentTags: string[],
  tags: ContentTagOption[],
  showHouses: boolean,
  house: House | ""
): string[] => {
  const allowed = new Set(
    tagsForLine(tags, showHouses, house).map((tag) => tag.code)
  );
  return contentTags.filter((code) => allowed.has(code));
};

export const proposalTotal = (projects: ProjectDraft[]): number =>
  projects.reduce((sum, project) => sum + effectiveAmount(project), 0);

const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

interface Props {
  projects: ProjectDraft[];
  onChange: (projects: ProjectDraft[]) => void;
  /** House Group tiene houses; BeApolo no. */
  showHouses: boolean;
  tags: ContentTagOption[];
}

/**
 * La tabla de proyectos de la propuesta.
 *
 * Los proyectos son varios y cada uno lleva su propio tipo de presupuesto, así que la
 * tabla no es una rejilla fija: cuando el tipo es `escenarios` la fila crece con hasta
 * tres importes y un radio para marcar el de referencia. El radio es exclusivo por
 * proyecto y no global: cada proyecto tiene su propia referencia.
 */
const ProjectsTable: React.FC<Props> = ({
  projects,
  onChange,
  showHouses,
  tags,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const update = (index: number, patch: Partial<ProjectDraft>) => {
    onChange(
      projects.map((project, i) =>
        i === index ? { ...project, ...patch } : project
      )
    );
  };

  const updateScenario = (
    projectIndex: number,
    scenarioIndex: number,
    patch: Partial<ScenarioDraft>
  ) => {
    const project = projects[projectIndex];
    update(projectIndex, {
      scenarios: project.scenarios.map((scenario, i) =>
        i === scenarioIndex ? { ...scenario, ...patch } : scenario
      ),
    });
  };

  const setReference = (projectIndex: number, scenarioIndex: number) => {
    const project = projects[projectIndex];
    // Exactamente uno: marcar otro desmarca el anterior. El backend rechaza cero o dos,
    // así que la pantalla no debe poder producir ninguno de los dos casos.
    update(projectIndex, {
      scenarios: project.scenarios.map((scenario, i) => ({
        ...scenario,
        isReference: i === scenarioIndex,
      })),
    });
  };

  const addScenario = (projectIndex: number) => {
    const project = projects[projectIndex];
    if (project.scenarios.length >= MAX_SCENARIOS) return;
    const isFirst = project.scenarios.length === 0;
    update(projectIndex, {
      scenarios: [
        ...project.scenarios,
        {
          label: "",
          amount: "",
          // El primero es la referencia por defecto: así nunca hay un proyecto con
          // escenarios y ninguna referencia, que el backend rechaza.
          isReference: isFirst,
        },
      ],
    });
  };

  const removeScenario = (projectIndex: number, scenarioIndex: number) => {
    const project = projects[projectIndex];
    const remaining = project.scenarios.filter((_, i) => i !== scenarioIndex);
    // Si se borra el de referencia, la referencia pasa al primero que quede.
    if (remaining.length > 0 && !remaining.some((s) => s.isReference)) {
      remaining[0] = { ...remaining[0], isReference: true };
    }
    update(projectIndex, { scenarios: remaining });
  };

  const changeBudgetType = (index: number, budgetType: ProjectBudgetType) => {
    if (budgetType === "ESCENARIOS") {
      // Al pasar a escenarios se limpia el importe suelto: el backend rechaza que vengan
      // los dos, porque entonces el total dependería de cuál gana.
      update(index, {
        budgetType,
        budget: "",
        scenarios:
          projects[index].scenarios.length > 0
            ? projects[index].scenarios
            : [{ label: "", amount: "", isReference: true }],
      });
      return;
    }
    update(index, { budgetType, scenarios: [] });
  };

  return (
    <Stack gap={2}>
      {projects.map((project, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t("commercialProposals.projects.project", "Proyecto")}{" "}
              {index + 1}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Chip
                size="small"
                label={euro.format(effectiveAmount(project))}
                color={effectiveAmount(project) > 0 ? "primary" : "default"}
              />
              {projects.length > 1 && (
                <Tooltip
                  title={t(
                    "commercialProposals.projects.remove",
                    "Quitar proyecto"
                  )}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      onChange(projects.filter((_, i) => i !== index))
                    }
                    sx={{ color: theme.palette.error.main }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <TextField
              size="small"
              label={t(
                "commercialProposals.projects.lineName",
                "Nombre de línea"
              )}
              value={project.lineName}
              onChange={(event) =>
                update(index, { lineName: event.target.value })
              }
              sx={{ flex: 2 }}
            />

            {showHouses && (
              <FormControl size="small" sx={{ flex: 1.2 }}>
                <InputLabel>
                  {t("commercialProposals.projects.house", "House")}
                </InputLabel>
                <Select
                  value={project.house}
                  label={t("commercialProposals.projects.house", "House")}
                  onChange={(event) =>
                    update(index, {
                      house: event.target.value as House,
                      contentTags: keepLineTags(
                        project.contentTags,
                        tags,
                        showHouses,
                        event.target.value as House
                      ),
                    })
                  }
                >
                  {HOUSES.map((house) => (
                    <MenuItem key={house} value={house}>
                      {/* El punto usa el color de marca de la house, el mismo que la
                          franja de la tarjeta del tablero: mismo lenguaje en las dos
                          pantallas. */}
                      <Box
                        component="span"
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: HOUSE_COLOR[house],
                          display: "inline-block",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      {HOUSE_LABEL[house]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ flex: 1.2 }}>
              <InputLabel>
                {t(
                  "commercialProposals.projects.budgetType",
                  "Tipo de presupuesto"
                )}
              </InputLabel>
              <Select
                value={project.budgetType}
                label={t(
                  "commercialProposals.projects.budgetType",
                  "Tipo de presupuesto"
                )}
                onChange={(event) =>
                  changeBudgetType(
                    index,
                    event.target.value as ProjectBudgetType
                  )
                }
              >
                <MenuItem value="DEFINIDO">
                  {t("commercialProposals.budgetType.DEFINIDO", "Definido")}
                </MenuItem>
                <MenuItem value="ESCENARIOS">
                  {t("commercialProposals.budgetType.ESCENARIOS", "Escenarios")}
                </MenuItem>
                <MenuItem value="ESTIMADO">
                  {t("commercialProposals.budgetType.ESTIMADO", "Estimado")}
                </MenuItem>
              </Select>
            </FormControl>

            {project.budgetType !== "ESCENARIOS" && (
              <TextField
                size="small"
                label={t("commercialProposals.projects.budget", "Presupuesto")}
                value={project.budget}
                onChange={(event) =>
                  update(index, { budget: event.target.value })
                }
                sx={{ flex: 1 }}
                inputProps={{ inputMode: "decimal" }}
              />
            )}
          </Stack>

          <FormControl
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            disabled={showHouses && !project.house}
          >
            <InputLabel>
              {t("commercialProposals.projects.contentTags", "Contenido")}
            </InputLabel>
            <Select
              multiple
              value={project.contentTags}
              onChange={(event) =>
                update(index, {
                  contentTags: event.target.value as string[],
                })
              }
              input={
                <OutlinedInput
                  label={t(
                    "commercialProposals.projects.contentTags",
                    "Contenido"
                  )}
                />
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((code) => (
                    <Chip
                      key={code}
                      size="small"
                      label={
                        tags.find((tag) => tag.code === code)?.label ?? code
                      }
                    />
                  ))}
                </Box>
              )}
            >
              {tagsForLine(tags, showHouses, project.house).map((tag) => (
                <MenuItem key={tag.code} value={tag.code}>
                  {tag.label}
                </MenuItem>
              ))}
            </Select>
            {showHouses && !project.house && (
              <FormHelperText>
                {t(
                  "commercialProposals.projects.contentTagsNeedHouse",
                  "Elige antes la House: cada una tiene su contenido"
                )}
              </FormHelperText>
            )}
          </FormControl>

          {project.budgetType === "ESCENARIOS" && (
            <Box mt={2}>
              <Divider sx={{ mb: 1.5 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="caption" color="text.secondary">
                  {t(
                    "commercialProposals.projects.scenariosHelp",
                    "Hasta tres escenarios. El marcado como referencia es el que cuenta para el total."
                  )}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addScenario(index)}
                  disabled={project.scenarios.length >= MAX_SCENARIOS}
                >
                  {t(
                    "commercialProposals.projects.addScenario",
                    "Añadir escenario"
                  )}
                </Button>
              </Stack>

              <Stack gap={1}>
                {project.scenarios.map((scenario, scenarioIndex) => (
                  <Stack
                    key={scenarioIndex}
                    direction="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Tooltip
                      title={t(
                        "commercialProposals.projects.reference",
                        "Escenario de referencia"
                      )}
                    >
                      <Radio
                        size="small"
                        checked={scenario.isReference}
                        onChange={() => setReference(index, scenarioIndex)}
                      />
                    </Tooltip>
                    <TextField
                      size="small"
                      label={t(
                        "commercialProposals.projects.scenarioLabel",
                        "Escenario"
                      )}
                      value={scenario.label}
                      onChange={(event) =>
                        updateScenario(index, scenarioIndex, {
                          label: event.target.value,
                        })
                      }
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      size="small"
                      label={t(
                        "commercialProposals.projects.scenarioAmount",
                        "Importe"
                      )}
                      value={scenario.amount}
                      onChange={(event) =>
                        updateScenario(index, scenarioIndex, {
                          amount: event.target.value,
                        })
                      }
                      sx={{ flex: 1 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                    {project.scenarios.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeScenario(index, scenarioIndex)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      ))}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          p: 2,
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={() => onChange([...projects, emptyProject()])}
        >
          {t("commercialProposals.projects.add", "Añadir proyecto")}
        </Button>
        <Box textAlign="right">
          <Typography variant="caption" color="text.secondary" display="block">
            {t(
              "commercialProposals.projects.total",
              "Presupuesto de propuesta"
            )}
          </Typography>
          <Typography variant="h6">
            {euro.format(proposalTotal(projects))}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

export default ProjectsTable;
